<!-- TOOLBAR -->
@if (showToolbar) {
  <div class="app-toolbar d-flex align-items-center justify-content-between px-3">
    <div>
      <span class="erp-page-title">{{ pageTitle }}</span>
      <span class="text-muted fs-8 ms-2 d-none d-md-inline">Accounts Audit & Closure Management</span>
    </div>
    <div class="d-flex align-items-center gap-2">
      <button type="button" class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center" (click)="exportToCsv()">
        <i class="bi bi-download me-1"></i> Export CSV
      </button>
      <button type="button" class="btn btn-sm btn-light border d-inline-flex align-items-center" (click)="fetchCustomerAccounts()" [disabled]="loading" title="Refresh">
        <i class="bi bi-arrow-clockwise me-1" [class.spin]="loading"></i> Refresh
      </button>
    </div>
  </div>
}

<div class="erp-page-layout">
  <!-- LEFT FILTER PANEL -->
  <div class="erp-filter-panel">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <span class="erp-section-title mb-0">Filters & Search</span>
      <button type="button" class="btn btn-link btn-sm text-decoration-none p-0 text-muted fs-8" (click)="clearFilters()">
        Reset All
      </button>
    </div>

    <!-- Search Input -->
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold text-uppercase text-muted">Contact Search</label>
      <div class="input-group input-group-sm">
        <span class="input-group-text bg-white border-end-0"><i class="bi bi-search text-muted"></i></span>
        <input 
          type="text" 
          class="form-control border-start-0 ps-0" 
          placeholder="Search by name..." 
          [ngModel]="contactName" 
          (ngModelChange)="onSearch($event)">
        @if (contactName) {
          <button class="btn btn-outline-secondary border-start-0" type="button" (click)="clearSearch()">
            <i class="bi bi-x"></i>
          </button>
        }
      </div>
    </div>

    <!-- Date Range Presets -->
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold text-uppercase text-muted">Date Preset</label>
      <div class="date-preset-grid d-flex flex-wrap gap-1 mb-2">
        <button type="button" class="btn btn-xs preset-btn" [class.active]="datePreset === 'this_year'" (click)="setDatePreset('this_year')">This Year</button>
        <button type="button" class="btn btn-xs preset-btn" [class.active]="datePreset === 'this_month'" (click)="setDatePreset('this_month')">This Month</button>
        <button type="button" class="btn btn-xs preset-btn" [class.active]="datePreset === 'last_30'" (click)="setDatePreset('last_30')">30 Days</button>
        <button type="button" class="btn btn-xs preset-btn" [class.active]="datePreset === 'all_time'" (click)="setDatePreset('all_time')">All Time</button>
      </div>

      <div class="row g-2">
        <div class="col-6">
          <label class="fs-9 text-muted mb-1">From</label>
          <input type="date" class="form-control form-control-sm" [(ngModel)]="startDate" (change)="onFilterChange()">
        </div>
        <div class="col-6">
          <label class="fs-9 text-muted mb-1">To</label>
          <input type="date" class="form-control form-control-sm" [(ngModel)]="endDate" (change)="onFilterChange()">
        </div>
      </div>
    </div>

    <!-- Status Filter -->
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold text-uppercase text-muted">Account Status</label>
      <select class="form-select form-select-sm" [(ngModel)]="contactStatus" (change)="onFilterChange()">
        <option value="">All Accounts</option>
        <option value="0">Open Accounts Only</option>
        <option value="1">Closed Accounts Only</option>
        <option value="pending">Pending Closure Approval Only</option>
      </select>
    </div>

    <!-- Balance / Debt Filter -->
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold text-uppercase text-muted">Financial Balance</label>
      <select class="form-select form-select-sm" [(ngModel)]="balanceFilter" (change)="setBalanceFilter(balanceFilter)">
        <option value="all">All Balances</option>
        <option value="due">Outstanding Due (> ₹0)</option>
        <option value="zero">Settled / Zero Balance (= ₹0)</option>
        <option value="advance">Credit / Advance (< ₹0)</option>
      </select>
    </div>

    <!-- Clear Filters Button -->
    <button type="button" class="btn btn-sm btn-light border w-100 mt-2 d-inline-flex align-items-center justify-content-center" (click)="clearFilters()">
      <i class="bi bi-x-circle me-1"></i> Clear All Filters
    </button>
  </div>

  <!-- RIGHT CONTENT PANEL -->
  <div class="erp-content-panel">

    <!-- KPI SUMMARY CARDS -->
    <div class="row g-3 mb-3">
      <div class="col-sm-6 col-xl-3">
        <div class="kpi-card app-card p-3">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span class="kpi-label text-muted fs-8 fw-semibold text-uppercase">Total Invoiced</span>
              <h5 class="kpi-value text-dark fw-bold mb-0 mt-1">{{ totalInvoiced | currency:'INR':'symbol':'1.0-0' }}</h5>
            </div>
            <div class="kpi-icon-wrap bg-primary-subtle text-primary">
              <i class="bi bi-receipt fs-4"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3">
        <div class="kpi-card app-card p-3">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span class="kpi-label text-muted fs-8 fw-semibold text-uppercase">Total Received</span>
              <h5 class="kpi-value text-success fw-bold mb-0 mt-1">{{ totalPaid | currency:'INR':'symbol':'1.0-0' }}</h5>
            </div>
            <div class="kpi-icon-wrap bg-success-subtle text-success">
              <i class="bi bi-wallet2 fs-4"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3">
        <div class="kpi-card app-card p-3 cursor-pointer" (click)="setBalanceFilter('due')">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span class="kpi-label text-muted fs-8 fw-semibold text-uppercase">Outstanding Due</span>
              <h5 class="kpi-value text-danger fw-bold mb-0 mt-1">{{ totalBalance | currency:'INR':'symbol':'1.0-0' }}</h5>
            </div>
            <div class="kpi-icon-wrap bg-danger-subtle text-danger">
              <i class="bi bi-exclamation-circle fs-4"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-6 col-xl-3">
        <div class="kpi-card app-card p-3 cursor-pointer" (click)="setQuickStatus('pending')">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span class="kpi-label text-muted fs-8 fw-semibold text-uppercase">Closure Requests</span>
              <h5 class="kpi-value text-warning fw-bold mb-0 mt-1">
                {{ pendingCount }} Pending
              </h5>
            </div>
            <div class="kpi-icon-wrap bg-warning-subtle text-warning">
              <i class="bi bi-hourglass-split fs-4"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN TABLE CARD -->
    <div class="app-card">
      <!-- Quick Filter Pills & Actions Bar -->
      <div class="card-header p-2 d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom">
        <div class="d-flex align-items-center gap-1 flex-wrap">
          <span class="text-muted fs-8 fw-semibold text-uppercase me-1">Quick:</span>
          <button 
            type="button" 
            class="tab-filter-btn" 
            [class.active]="contactStatus === '' && balanceFilter === 'all'" 
            (click)="setQuickStatus('')">
            All ({{ customerAccounts.length }})
          </button>
          <button 
            type="button" 
            class="tab-filter-btn" 
            [class.active]="contactStatus === '0'" 
            (click)="setQuickStatus('0')">
            Open ({{ openCount }})
          </button>
          <button 
            type="button" 
            class="tab-filter-btn" 
            [class.active]="contactStatus === '1'" 
            (click)="setQuickStatus('1')">
            Closed ({{ closedCount }})
          </button>
          <button 
            type="button" 
            class="tab-filter-btn pending-btn" 
            [class.active]="contactStatus === 'pending'" 
            (click)="setQuickStatus('pending')">
            <i class="bi bi-hourglass-split me-1"></i>Pending Closure ({{ pendingCount }})
          </button>
          <button 
            type="button" 
            class="tab-filter-btn" 
            [class.active]="balanceFilter === 'due'" 
            (click)="setBalanceFilter(balanceFilter === 'due' ? 'all' : 'due')">
            Due Only ({{ dueCount }})
          </button>
        </div>

        <div class="d-flex align-items-center gap-2">
          @if (contactName) {
            <span class="badge bg-light text-dark border">Search: "{{ contactName }}"</span>
          }
          <button type="button" class="btn btn-sm btn-light border" (click)="exportToCsv()" title="Export filtered accounts to CSV">
            <i class="bi bi-file-earmark-spreadsheet me-1 text-success"></i>CSV
          </button>
          <button type="button" class="btn btn-sm btn-light border" (click)="fetchCustomerAccounts()" [disabled]="loading" title="Refresh accounts">
            <i class="bi bi-arrow-clockwise" [class.spin]="loading"></i>
          </button>
        </div>
      </div>

      <!-- Table Body -->
      <div class="app-table-container">
        @if (loading) {
          <div class="erp-loading-overlay py-5 text-center">
            <div class="spinner-border text-primary mb-2"></div>
            <p class="text-muted fs-7">Loading account records & closure statuses...</p>
          </div>
        }

        @if (!loading && displayedAccounts.length > 0) {
          <table class="table app-table table-hover app-table-sticky mb-0">
            <thead>
              <tr>
                <th class="cursor-pointer" (click)="sort('name')">
                  Contact <i class="bi ms-1" [ngClass]="sortColumn === 'name' ? (sortDirection === 'asc' ? 'bi-arrow-down' : 'bi-arrow-up') : ''"></i>
                </th>
                <th class="td-currency cursor-pointer text-end" (click)="sort('total_amount')">
                  Total Invoiced <i class="bi ms-1" [ngClass]="sortColumn === 'total_amount' ? (sortDirection === 'asc' ? 'bi-arrow-down' : 'bi-arrow-up') : ''"></i>
                </th>
                <th class="td-currency cursor-pointer text-end" (click)="sort('total_paid')">
                  Total Paid <i class="bi ms-1" [ngClass]="sortColumn === 'total_paid' ? (sortDirection === 'asc' ? 'bi-arrow-down' : 'bi-arrow-up') : ''"></i>
                </th>
                <th class="td-currency cursor-pointer text-end" (click)="sort('balance')">
                  Balance <i class="bi ms-1" [ngClass]="sortColumn === 'balance' ? (sortDirection === 'asc' ? 'bi-arrow-down' : 'bi-arrow-up') : ''"></i>
                </th>
                <th class="cursor-pointer" (click)="sort('created_at')">
                  Created Date <i class="bi ms-1" [ngClass]="sortColumn === 'created_at' ? (sortDirection === 'asc' ? 'bi-arrow-down' : 'bi-arrow-up') : ''"></i>
                </th>
                <th class="cursor-pointer" (click)="sort('account_status')">
                  Status <i class="bi ms-1" [ngClass]="sortColumn === 'account_status' ? (sortDirection === 'asc' ? 'bi-arrow-down' : 'bi-arrow-up') : ''"></i>
                </th>
                <th class="text-end">Closure Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (customer of displayedAccounts; track customer.contact_id) {
                <tr [class.table-warning-subtle]="isPendingClosure(customer)">
                  <!-- Name & Avatar -->
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="account-mini-avatar me-2 flex-shrink-0">
                        {{ getInitials(customer.name) }}
                      </div>
                      <div class="min-w-0">
                        <a [routerLink]="['/hrm', customer.contact_type || contactType, 'view', customer.contact_id]" class="fw-semibold text-primary text-decoration-none d-block text-truncate">
                          {{ customer.name }}
                        </a>
                        <span class="fs-8 text-muted">#{{ customer.contact_id }}</span>
                      </div>
                    </div>
                  </td>

                  <!-- Invoiced -->
                  <td class="td-currency text-end fw-500">
                    {{ customer.total_amount | currency:'INR' }}
                  </td>

                  <!-- Paid -->
                  <td class="td-currency text-end text-success fw-500">
                    {{ customer.total_paid | currency:'INR' }}
                  </td>

                  <!-- Balance -->
                  <td class="td-currency text-end fw-bold" [class.text-danger]="customer.balance > 0" [class.text-success]="customer.balance <= 0">
                    {{ customer.balance | currency:'INR' }}
                    @if (customer.balance > 0) {
                      <i class="bi bi-exclamation-circle-fill text-danger ms-1 fs-9" title="Outstanding Due"></i>
                    }
                  </td>

                  <!-- Created At -->
                  <td class="text-muted fs-8">
                    {{ customer.created_at | date:'dd MMM yyyy' }}
                  </td>

                  <!-- Status Pill -->
                  <td>
                    @if (isPendingClosure(customer)) {
                      <span class="badge-status-pill pending" title="Closure request #{{ customer.latest_closure_log?.request_id }} is waiting for approval">
                        <i class="bi bi-hourglass-split me-1"></i>Pending Approval
                      </span>
                    } @else if (isAccountClosed(customer)) {
                      <span class="badge-status-pill closed" title="Account is officially closed">
                        <i class="bi bi-lock-fill me-1"></i>Closed
                      </span>
                    } @else {
                      <span class="badge-status-pill open" title="Account is active and open">
                        <i class="bi bi-check-circle-fill me-1"></i>Open
                      </span>
                    }
                  </td>

                  <!-- Closure Workflow Actions -->
                  <td class="text-end">
                    <div class="d-inline-flex align-items-center gap-1">
                      <!-- Open: Can Request Closure -->
                      @if (canRequestClosure(customer)) {
                        <button 
                          type="button" 
                          class="btn btn-xs btn-outline-danger d-inline-flex align-items-center"
                          (click)="openRequestModal(requestModal, customer)"
                          title="Submit request to close this account">
                          <i class="bi bi-lock me-1"></i>Close Req
                        </button>
                      }

                      <!-- Pending Closure (Waiting for Approval): Show Approve or Reject when Admin, else normal user behavior -->
                      @if (isPendingClosure(customer)) {
                        @if (isAdmin) {
                          <!-- Admin: Show Approve -->
                          <button 
                            type="button" 
                            class="btn btn-xs btn-success d-inline-flex align-items-center"
                            (click)="openApproveModal(approveModal, customer)"
                            [title]="'Approve Closure Request #' + customer.latest_closure_log?.request_id">
                            <i class="bi bi-check-lg me-1"></i>Approve
                          </button>

                          <!-- Admin: Show Reject -->
                          <button 
                            type="button" 
                            class="btn btn-xs btn-outline-danger d-inline-flex align-items-center"
                            (click)="openRejectModal(rejectModal, customer)"
                            title="Reject closure request #{{ customer.latest_closure_log?.request_id }}">
                            <i class="bi bi-x-lg me-1"></i>Reject
                          </button>

                          <!-- Admin: Cancel request -->
                          <button 
                            type="button" 
                            class="btn btn-xs btn-outline-secondary d-inline-flex align-items-center"
                            (click)="openCancelModal(cancelModal, customer)"
                            title="Cancel closure request #{{ customer.latest_closure_log?.request_id }}">
                            <i class="bi bi-slash-circle"></i>
                          </button>
                        } @else {
                          <!-- Normal user behavior: cannot approve or reject. Show Cancel if can cancel -->
                          @if (canCancelClosure(customer)) {
                            <button 
                              type="button" 
                              class="btn btn-xs btn-outline-secondary d-inline-flex align-items-center"
                              (click)="openCancelModal(cancelModal, customer)"
                              title="Cancel closure request #{{ customer.latest_closure_log?.request_id }}">
                              <i class="bi bi-slash-circle me-1"></i>Cancel
                            </button>
                          }
                        }
                      }

                      <!-- Closure Audit Trail History Icon -->
                      <button 
                        type="button" 
                        class="btn btn-xs btn-light border d-inline-flex align-items-center"
                        (click)="openHistoryModal(historyModal, customer)"
                        title="View Account Closure Audit Log">
                        <i class="bi bi-clock-history"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else if (!loading && displayedAccounts.length === 0) {
          <div class="app-table-empty py-5 text-center">
            <i class="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
            <h6 class="fw-semibold text-dark">No accounts found</h6>
            <p class="text-muted fs-8 mb-3">No account records match your selected filters and search query.</p>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearFilters()">
              Clear All Filters
            </button>
          </div>
        }
      </div>

      <!-- TABLE FOOTER / PAGINATION -->
      <div class="app-table-footer p-2 d-flex align-items-center justify-content-between flex-wrap gap-2 border-top">
        @if (!loading && totalRecords > 0) {
          <div class="d-flex align-items-center gap-2">
            <span class="text-muted fs-8">Per page:</span>
            <select class="form-select form-select-sm w-auto" [(ngModel)]="perPage" (change)="onPerPageChange()">
              <option [value]="10">10</option>
              <option [value]="15">15</option>
              <option [value]="25">25</option>
              <option [value]="50">50</option>
              <option [value]="100">100</option>
            </select>
            <span class="text-muted fs-8">Page <strong>{{ currentPage }}</strong> of {{ totalPages }} ({{ totalRecords }} total records)</span>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-light border" [disabled]="currentPage === 1" (click)="onPageChange(currentPage - 1)">
              <i class="bi bi-chevron-left"></i> Previous
            </button>
            <button class="btn btn-sm btn-light border" [disabled]="currentPage === totalPages" (click)="onPageChange(currentPage + 1)">
              Next <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        }
      </div>
    </div>
  </div>
</div>

<!-- ========================================================================= -->
<!-- MODAL: REQUEST ACCOUNT CLOSURE -->
<!-- ========================================================================= -->
<ng-template #requestModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">
      <i class="bi bi-lock text-danger me-2"></i>Request Account Closure
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p class="mb-3">
      Submit a formal closure request for 
      <strong class="text-dark">{{ activeContact?.name }}</strong> 
      (#{{ activeContact?.contact_id }}).
    </p>

    <!-- Account Balance Info -->
    @if (activeContact) {
      <div class="alert alert-light border d-flex align-items-center py-2 mb-3">
        <i class="bi bi-wallet2 fs-5 me-2 text-primary flex-shrink-0"></i>
        <div class="fs-8">
          <strong>Current Balance:</strong> {{ activeContact.balance | currency:'INR' }}
        </div>
      </div>
    }

    <!-- Comments Textarea -->
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold">Reason / Comments (Optional)</label>
      <textarea 
        class="form-control" 
        rows="3" 
        [(ngModel)]="closureActionComments" 
        placeholder="Enter explanation or reason for requesting account closure...">
      </textarea>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-sm btn-light border" (click)="modal.dismiss()" [disabled]="closureActionSubmitting">
      Cancel
    </button>
    <button type="button" class="btn btn-sm btn-danger d-inline-flex align-items-center" (click)="submitRequestModal(modal)" [disabled]="closureActionSubmitting">
      @if (closureActionSubmitting) {
        <span class="spinner-border spinner-border-sm me-1"></span> Submitting...
      } @else {
        <i class="bi bi-send me-1"></i> Submit Closure Request
      }
    </button>
  </div>
</ng-template>

<!-- ========================================================================= -->
<!-- MODAL: APPROVE ACCOUNT CLOSURE -->
<!-- ========================================================================= -->
<ng-template #approveModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold text-success">
      <i class="bi bi-check-circle-fill me-2"></i>Approve Account Closure
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p class="mb-3">
      Are you sure you want to approve closure of 
      <strong class="text-dark">{{ activeContact?.name }}</strong>?
    </p>
    <div class="alert alert-info py-2 mb-3 fs-8">
      <i class="bi bi-info-circle me-1"></i>
      The contact account status will be permanently marked as <strong>Closed</strong>.
    </div>
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold">Approval Notes (Optional)</label>
      <textarea 
        class="form-control" 
        rows="2" 
        [(ngModel)]="closureActionComments" 
        placeholder="e.g. Account audit verified, approved.">
      </textarea>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-sm btn-light border" (click)="modal.dismiss()" [disabled]="closureActionSubmitting">
      Cancel
    </button>
    <button type="button" class="btn btn-sm btn-success d-inline-flex align-items-center" (click)="submitApproveModal(modal)" [disabled]="closureActionSubmitting">
      @if (closureActionSubmitting) {
        <span class="spinner-border spinner-border-sm me-1"></span> Approving...
      } @else {
        <i class="bi bi-check-lg me-1"></i> Confirm Approval
      }
    </button>
  </div>
</ng-template>

<!-- ========================================================================= -->
<!-- MODAL: REJECT ACCOUNT CLOSURE -->
<!-- ========================================================================= -->
<ng-template #rejectModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold text-danger">
      <i class="bi bi-x-circle-fill me-2"></i>Reject Closure Request
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p class="mb-3">
      Reject closure request for <strong class="text-dark">{{ activeContact?.name }}</strong>.
      Please provide a reason:
    </p>
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold text-danger">Rejection Reason (Required) *</label>
      <textarea 
        class="form-control" 
        rows="3" 
        [(ngModel)]="closureActionComments" 
        placeholder="Specify why this closure request is being rejected...">
      </textarea>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-sm btn-light border" (click)="modal.dismiss()" [disabled]="closureActionSubmitting">
      Cancel
    </button>
    <button type="button" class="btn btn-sm btn-danger d-inline-flex align-items-center" (click)="submitRejectModal(modal)" [disabled]="closureActionSubmitting || !closureActionComments.trim()">
      @if (closureActionSubmitting) {
        <span class="spinner-border spinner-border-sm me-1"></span> Rejecting...
      } @else {
        <i class="bi bi-x-lg me-1"></i> Reject Request
      }
    </button>
  </div>
</ng-template>

<!-- ========================================================================= -->
<!-- MODAL: CANCEL ACCOUNT CLOSURE -->
<!-- ========================================================================= -->
<ng-template #cancelModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold text-secondary">
      <i class="bi bi-slash-circle me-2"></i>Cancel Closure Request
    </h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p class="mb-3">
      Are you sure you want to cancel the pending closure request for 
      <strong class="text-dark">{{ activeContact?.name }}</strong>?
    </p>
    <div class="mb-3">
      <label class="form-label fs-8 fw-semibold">Cancellation Comments (Optional)</label>
      <textarea 
        class="form-control" 
        rows="2" 
        [(ngModel)]="closureActionComments" 
        placeholder="e.g. Cancelled at client request.">
      </textarea>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-sm btn-light border" (click)="modal.dismiss()" [disabled]="closureActionSubmitting">
      Close
    </button>
    <button type="button" class="btn btn-sm btn-secondary d-inline-flex align-items-center" (click)="submitCancelModal(modal)" [disabled]="closureActionSubmitting">
      @if (closureActionSubmitting) {
        <span class="spinner-border spinner-border-sm me-1"></span> Cancelling...
      } @else {
        <i class="bi bi-trash me-1"></i> Confirm Cancel
      }
    </button>
  </div>
</ng-template>

<!-- ========================================================================= -->
<!-- MODAL: CLOSURE AUDIT TRAIL HISTORY -->
<!-- ========================================================================= -->
<ng-template #historyModal let-modal>
  <div class="modal-header">
    <div>
      <h5 class="modal-title fw-bold mb-0">
        <i class="bi bi-clock-history text-primary me-2"></i>Account Closure Audit Log
      </h5>
      <small class="text-muted">History for <strong>{{ historyContactName }}</strong></small>
    </div>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    @if (historyLoading) {
      <div class="text-center py-4 text-muted">
        <div class="spinner-border spinner-border-sm text-primary mb-2"></div>
        <div>Loading closure audit history...</div>
      </div>
    }

    @if (!historyLoading && closureHistory.length === 0) {
      <div class="text-center py-5 border rounded-2 bg-light">
        <i class="bi bi-journal-x fs-1 text-muted d-block mb-2"></i>
        <h6 class="fw-semibold text-muted">No Closure History Found</h6>
        <p class="fs-8 text-muted mb-0">This contact has no account closure requests or historical logs recorded.</p>
      </div>
    }

    @if (!historyLoading && closureHistory.length > 0) {
      <div class="timeline-history-list">
        @for (item of closureHistory; track item.request_id || item.id || $index) {
          <div class="timeline-item d-flex gap-3 pb-3 mb-3 border-bottom">
            <div class="timeline-icon-wrap flex-shrink-0">
              <div class="timeline-status-icon" [ngClass]="item.status">
                @if (item.status === 'closed') {
                  <i class="bi bi-check-circle-fill text-success"></i>
                } @else if (item.status === 'waiting_for_approval') {
                  <i class="bi bi-hourglass-split text-warning"></i>
                } @else if (item.status === 'rejected') {
                  <i class="bi bi-x-circle-fill text-danger"></i>
                } @else {
                  <i class="bi bi-slash-circle-fill text-secondary"></i>
                }
              </div>
            </div>

            <div class="flex-grow-1 min-w-0">
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1">
                <div>
                  <span class="badge-status-pill me-2" [ngClass]="item.status === 'closed' ? 'closed' : (item.status === 'waiting_for_approval' ? 'pending' : '')">
                    {{ item.status | uppercase }}
                  </span>
                  @if (item.request_id) {
                    <span class="fs-8 text-muted">Req #{{ item.request_id }}</span>
                  }
                </div>
                <span class="fs-8 text-muted">
                  {{ item.created_at | date:'dd MMM yyyy, hh:mm a' }}
                </span>
              </div>

              @if (item.requester) {
                <div class="fs-8 text-muted mb-1">
                  <i class="bi bi-person me-1"></i>Requester: 
                  <strong>{{ item.requester.firstname || item.requester.name }} {{ item.requester.lastname || '' }}</strong> 
                  <span class="ms-1">({{ item.requester.email }})</span>
                </div>
              }

              @if (item.comments) {
                <div class="timeline-comment-box p-2 rounded bg-light border fs-8 mt-1">
                  {{ item.comments }}
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-sm btn-secondary" (click)="modal.dismiss()">
      Close
    </button>
  </div>
</ng-template>
